import { Button, Flex, Separator, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { SectionCard } from '~/components/SectionCard'
import { LeakTriggerConfigForm } from './LeakTriggerConfigForm'
import { WaterStation } from '../../schemas'
import { Fragment, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DataSource, StationLeakTriggerConfigs, StationLeakTriggersConfigSchema } from '@willie/schemas'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateWaterStation } from '../../service'
import { toast } from 'sonner'
import { TRPCInputs, useTRPCClient } from '~/utils/trpc'
import _ from 'lodash'
import { useTopicSubscribersState } from '~/features/notifications/hooks/useTopicSubscribersState'

type StationLeakTriggerConfigsProps = {
  station: WaterStation
  datasources: DataSource[]
}

type TriggerKey = keyof StationLeakTriggerConfigs

const LeakTriggerConfigSection = ({ station, datasources }: StationLeakTriggerConfigsProps) => {
  const { t } = useTranslation('sites')
  const queryClient = useQueryClient()
  const trpc = useTRPCClient()

  const methods = useForm({
    resolver: zodResolver(StationLeakTriggersConfigSchema),
    defaultValues: station.leakTriggerConfig,
    mode: 'onChange'
  })
  const {
    handleSubmit,
    formState: { isDirty, isValid }
  } = methods

  // Dynamically create subscriber state hooks for each trigger key
  const subscriberStates = Object.keys(station.leakTriggerConfig).reduce<
    Record<TriggerKey, ReturnType<typeof useTopicSubscribersState>>
  >(
    (acc, key) => {
      const triggerKey = key as TriggerKey
      acc[triggerKey] = useTopicSubscribersState({
        scope: 'site',
        id: station.site_id,
        notificationTrigger: triggerKey,
        resourceID: station.id
      })
      return acc
    },
    {} as Record<TriggerKey, ReturnType<typeof useTopicSubscribersState>>
  )

  // Check if any subscription is dirty
  const subscriptionsDirty = useMemo(
    () => Object.values(subscriberStates).some((state) => state.isDirty),
    [subscriberStates]
  )

  // Handler for member selection changes
  const handleMemberSelectionChange = (triggerKey: TriggerKey, selectedMembers: string[]) => {
    subscriberStates[triggerKey]?.setSubscribedMembers(selectedMembers)
  }

  // Get current subscribers for a specific trigger
  const getSubscribedMembersForTrigger = (triggerKey: TriggerKey): string[] => {
    return subscriberStates[triggerKey]?.subscribedMembers || []
  }

  // Handle updating all subscriptions for all triggers
  const updateAllSubscriptions = async () => {
    // Collect all dirty trigger states

    const memberChanges: TRPCInputs['orgMembers']['updateMemberTopics'][] = _(subscriberStates)
      .entries()
      .flatMap(([triggerKey, state]) =>
        state.operations.map((op) => ({ ...op, triggerKey: triggerKey as TriggerKey }))
      )
      .groupBy('memberID')
      .mapValues((ops) => {
        const topics: TRPCInputs['orgMembers']['updateMemberTopics']['topics'] = ops.map((op) => ({
          notificationTrigger: op.triggerKey,
          resourceID: station.id,
          action: op.operation
        }))
        return {
          memberId: ops[0].memberID,
          scope: 'site' as const,
          scopeId: station.site_id,
          topics
        }
      })
      .values()
      .value()

    if (memberChanges.length === 0) {
      return
    }

    try {
      await Promise.all(memberChanges.map((change) => trpc.orgMembers.updateMemberTopics.mutate(change)))
    } catch (error) {
      console.error('Error updating member subscriptions:', error)
      throw error
    }
  }

  // Combined mutation for saving both trigger configs and member subscriptions
  const { mutate: saveAll, isPending } = useMutation({
    mutationFn: async (leakTriggerConfig: StationLeakTriggerConfigs) => {
      // First save the leak trigger config
      const updatedStation = {
        ...station,
        leakTriggerConfig
      }
      await updateWaterStation(updatedStation)

      // Then update subscriptions if needed
      if (subscriptionsDirty) {
        await updateAllSubscriptions()
      }
    },
    onSuccess: () => {
      toast.success(t('stations.leakTriggers.saveSuccess'))
      queryClient.invalidateQueries({
        queryKey: ['station', station.id]
      })
      // should we invalidate the subscriptions queries as well
    },
    onError: (error) => {
      console.error('Error updating leak trigger configs and subscriptions', error)
      toast.error(t('stations.leakTriggers.saveError'))
    }
  })

  const onSubmit = handleSubmit((data) => {
    saveAll(data)
  })

  const isLeakDetectionAvailable = datasources.some(
    (ds) => ds.sourceType === 'meter' && ds.config.maker === 'shayp'
  )

  if (!isLeakDetectionAvailable) {
    return null
  }

  // Determine if form should be enabled for saving
  const isFormDirty = isDirty || subscriptionsDirty
  const isFormValid = isValid // Add any additional validation for subscriptions if needed

  return (
    <SectionCard title={t('stations.leakTriggers.title')}>
      <Text size="2" color="gray">
        {t('stations.leakTriggers.description')}
      </Text>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Flex direction="column" gap="6">
            {Object.keys(station.leakTriggerConfig).map((key) => {
              const triggerKey = key as TriggerKey
              return (
                <Fragment key={triggerKey}>
                  <Separator size={'4'} />
                  <LeakTriggerConfigForm
                    triggerKey={triggerKey}
                    onTopicSubscriberChange={handleMemberSelectionChange.bind(null, triggerKey)}
                    subscribedMembers={getSubscribedMembersForTrigger(triggerKey)}
                  />
                </Fragment>
              )
            })}
            <Separator size={'4'} />

            <Flex justify="end">
              <Button
                size={'3'}
                type="submit"
                variant="solid"
                disabled={!isFormDirty || !isFormValid}
                loading={isPending}
              >
                {t('stations.leakTriggers.saveButton')}
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </SectionCard>
  )
}

export default LeakTriggerConfigSection
